/** One question/answer pair from the public FAQ Dictionary. */
export interface FaqItem {
	q: string
	a: string
}

/** A category of Q&A — a labelled group of {@link FaqItem}s. */
export interface FaqCategory {
	label: string
	items: FaqItem[]
}

/** The live filter state driving the `/faq` search + category chips (EPT-274). */
export interface FaqFilter {
	/** Free-text query; substring-matched over each item's `q` + `a`. */
	query: string
	/** The single selected category label, or `null` for the "All" default. */
	category: string | null
}

/**
 * The pure filter seam behind the `/faq` search/filter layer (design-lane 1f).
 * Narrows the grouped Dictionary items by an optional single-selected `category`
 * and a case-insensitive substring `query` over each item's question and answer.
 * Groups left with no matching items drop out entirely, so an empty result is the
 * no-results signal the route swaps for `EmptyState`. Whitespace-only queries are
 * treated as empty. The input is never mutated — each surviving group is a copy.
 */
export function filterFaqCategories(
	categories: FaqCategory[],
	{ query, category }: FaqFilter,
): FaqCategory[] {
	const needle = query.trim().toLowerCase()
	return categories
		.filter((c) => category === null || c.label === category)
		.map((c) => ({
			...c,
			items: c.items.filter(
				(item) =>
					needle === '' ||
					item.q.toLowerCase().includes(needle) ||
					item.a.toLowerCase().includes(needle),
			),
		}))
		.filter((c) => c.items.length > 0)
}

/** Total number of items across the (already filtered) categories. */
export function countFaqItems(categories: FaqCategory[]): number {
	return categories.reduce((total, c) => total + c.items.length, 0)
}

/**
 * The Dictionary's Q&A flattened across categories, in declaration order. The
 * grouped `faq.categories` is the single source of truth for both the `/faq`
 * page and the home teaser (EPT-275); the teaser takes the first N of this flat
 * list, so a derived app that reorders or adds questions updates both surfaces
 * from one place.
 */
export function flattenFaqItems(categories: FaqCategory[]): FaqItem[] {
	return categories.flatMap((c) => c.items)
}
